import React, { useEffect, useRef, useState } from 'react';
import './AnalyticsProfilePicker.css';

export type AnalyticsProfileLite = { id: string; name: string };

type Props = {
  profiles: AnalyticsProfileLite[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
};

/** Multi-select profiles (checkboxes). At least one must stay selected. */
const AnalyticsProfilePicker: React.FC<Props> = ({ profiles, selectedIds, onChange }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (open && rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  if (profiles.length <= 1) return null;

  const orderedSelected = profiles.filter((p) => selectedIds.includes(p.id)).map((p) => p.id);

  const toggle = (id: string) => {
    if (orderedSelected.includes(id)) {
      if (orderedSelected.length <= 1) return;
      onChange(orderedSelected.filter((x) => x !== id));
    } else {
      onChange([...orderedSelected, id]);
    }
  };

  const summary =
    orderedSelected.length >= profiles.length
      ? 'All profiles'
      : orderedSelected
          .map((id) => profiles.find((p) => p.id === id)?.name)
          .filter(Boolean)
          .join(', ');

  return (
    <div className="analytics-profile-picker toolbar-row" ref={rootRef}>
      <span className="toolbar-label">Profile</span>
      <div className="analytics-profile-picker-inner">
        <button
          type="button"
          className="analytics-profile-picker-trigger"
          aria-expanded={open}
          aria-haspopup="listbox"
          onClick={() => setOpen((o) => !o)}
        >
          <span className="analytics-profile-picker-summary">{summary || 'Select…'}</span>
          <span className="analytics-profile-picker-caret" aria-hidden>
            ▾
          </span>
        </button>
        {open && (
          <div className="analytics-profile-picker-menu" role="listbox" aria-multiselectable>
            {profiles.map((p) => (
              <label key={p.id} className="analytics-profile-picker-option">
                <input
                  type="checkbox"
                  checked={orderedSelected.includes(p.id)}
                  onChange={() => toggle(p.id)}
                />
                <span>{p.name}</span>
              </label>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalyticsProfilePicker;
